/**
 * Task V2 completion modal — Storyblok Visual Editor preview.
 *
 * This is the screen a member sees when they tap "Complete" on a Task V2 action:
 * a title, a description, one row per input field, and the completion button.
 *
 * WHY IT EXISTS
 * A completion story is a `fields` list of blocks whose only visible difference
 * in the Storyblok side panel is their order. An author editing "attestation_2"
 * had no way to tell which sentence they were changing without publishing and
 * opening the app. Every block here is wrapped with `storyblokEditable()`, so
 * clicking a row in the iframe selects THAT block's form in the side panel.
 *
 * ORDER
 * Rendered in the story's own `fields` order, which is the order the member
 * sees. The three reference modals all run calendar → input/dropdown →
 * attestation → CTA, but that is a convention, not a rule the data enforces, so
 * this reflects the data rather than re-sorting it.
 */

import { Box, Typography } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { storyblokEditable } from '@storyblok/react';
import { readOptionValues, type DropdownOptionBlok } from '../../lib/dropdownOptions';
import { flattenRichTextDeep } from './htmlPreview';

const COLORS = {
  text: '#0D1C3D',
  muted: '#4A546A',
  border: '#DADADA',
  ground: '#FFFFFF',
  field: '#F7F8FA',
  accent: '#1B6EF3',
  placeholder: '#8A93A6',
};

export interface CompletionUiBlok {
  component?: string;
  title?: string;
  description?: string;
  cta_label?: string;
  warning_label?: string;
  fields?: FieldBlok[];
  _uid?: string;
}

export interface FieldBlok {
  component?: string;
  field_code?: string;
  label?: string;
  placeholder?: string;
  /** Dropdown choices. Authored in the completion-UI dialog and carried in the
   *  block, the same way this block carries its label — consumers read the
   *  block, so choices kept anywhere else are invisible to them.
   *
   *  A `bloks` field per the Storyblok schema — the only shape Storyblok can
   *  edit. Read through `readOptionValues`, which also accepts the bare
   *  `string[]` older stories hold rather than failing the render. */
  options?: DropdownOptionBlok[] | string[];
  /** INTERACTIVE: the unit shown beside the dial, and the manual-entry copy. */
  unit_label?: string;
  manual_input_label?: string;
  manual_input_placeholder?: string;
  manual_input_description?: string;
  description?: string;
  help_text?: string;
  title?: string;
  unit?: string;
  error_message?: string;
  cta_label?: string;
  log_label?: string;
  save_label?: string;
  activity_log_label?: string;
  missing_activity_label?: string;
  log_activity_label?: string;
  _uid?: string;
}

const editable = (blok: unknown): Record<string, string> =>
  (blok ? (storyblokEditable(blok as Parameters<typeof storyblokEditable>[0]) as Record<string, string>) : {});

/** The field code, shown small. Without it two attestations are indistinguishable. */
function CodeTag({ code }: { code?: string }) {
  return (
    <Typography
      component="span"
      sx={{ fontSize: 10.5, fontFamily: 'monospace', color: COLORS.placeholder, ml: 0.75 }}
    >
      {code || 'no field code'}
    </Typography>
  );
}

function LabelRow({ label, help, code }: { label?: string; help?: string; code?: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>
        {label || 'Untitled field'}
      </Typography>
      {help ? <InfoOutlinedIcon sx={{ fontSize: 14, color: COLORS.muted }} titleAccess={help} /> : null}
      <CodeTag code={code} />
    </Box>
  );
}

const fieldBoxSx = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: '10px',
  bgcolor: COLORS.field,
  px: 1.5,
  py: 1.25,
  display: 'flex',
  alignItems: 'center',
  gap: 1,
} as const;

function CalendarField({ blok }: { blok: FieldBlok }) {
  return (
    <Box {...editable(blok)}>
      <LabelRow label={blok.label} help={blok.help_text} code={blok.field_code} />
      <Box sx={fieldBoxSx}>
        <Typography sx={{ flexGrow: 1, fontSize: 14, color: COLORS.placeholder }}>
          {blok.placeholder || 'mm/dd/yyyy'}
        </Typography>
        <CalendarTodayIcon sx={{ fontSize: 17, color: COLORS.muted }} />
      </Box>
    </Box>
  );
}

function DropdownField({ blok }: { blok: FieldBlok }) {
  const options = readOptionValues(blok.options);
  return (
    <Box {...editable(blok)}>
      <LabelRow label={blok.label} help={blok.help_text} code={blok.field_code} />
      <Box sx={fieldBoxSx}>
        <Typography sx={{ flexGrow: 1, fontSize: 14, color: COLORS.placeholder }}>
          {blok.placeholder || 'Select'}
        </Typography>
        <ExpandMoreIcon sx={{ fontSize: 19, color: COLORS.muted }} />
      </Box>
      {/* The choices themselves, so an author can see what a member will get
          rather than an empty box. A dropdown with none is worth showing as
          such — that is the exact state this whole fix was reported for. */}
      <Typography sx={{ mt: 0.5, fontSize: 12, color: options.length ? COLORS.muted : COLORS.placeholder }}>
        {options.length ? options.join(' · ') : 'No options yet'}
      </Typography>
    </Box>
  );
}

function InputField({ blok }: { blok: FieldBlok }) {
  return (
    <Box {...editable(blok)}>
      <LabelRow label={blok.label} help={blok.help_text} code={blok.field_code} />
      <Box sx={fieldBoxSx}>
        <Typography sx={{ flexGrow: 1, fontSize: 14, color: COLORS.placeholder }}>
          {blok.placeholder || 'Enter a value'}
        </Typography>
        {blok.unit ? (
          <Typography sx={{ fontSize: 13, color: COLORS.muted }}>{blok.unit}</Typography>
        ) : null}
      </Box>
      {blok.error_message ? (
        <Typography sx={{ fontSize: 11.5, color: '#B3261E', mt: 0.5 }}>{blok.error_message}</Typography>
      ) : null}
    </Box>
  );
}

function AttestationField({ blok }: { blok: FieldBlok }) {
  return (
    <Box {...editable(blok)} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <Box sx={{
        width: 18, height: 18, mt: '2px', flexShrink: 0,
        border: `1.5px solid ${COLORS.muted}`, borderRadius: '4px',
      }} />
      <Box>
        <Typography sx={{ fontSize: 13, color: COLORS.text, lineHeight: 1.45 }}>
          {blok.title || 'Attestation text not set'}
          <CodeTag code={blok.field_code} />
        </Typography>
        {blok.cta_label ? (
          <Typography sx={{ fontSize: 12.5, color: COLORS.accent, mt: 0.25 }}>{blok.cta_label}</Typography>
        ) : null}
      </Box>
    </Box>
  );
}

function InteractiveField({ blok }: { blok: FieldBlok }) {
  const rows = [
    ['Description', blok.description],
    ['Unit', blok.unit_label],
    ['Manual label', blok.manual_input_label],
    ['Manual hint', blok.manual_input_placeholder],
    ['Manual note', blok.manual_input_description],
    // Older stories were authored with journal-style copy. Nothing writes
    // these any more, but a story that still carries them should show what it
    // holds rather than look blank.
    ['Activity log', blok.activity_log_label],
    ['No activity', blok.missing_activity_label],
    ['Log activity', blok.log_activity_label],
    ['CTA', blok.cta_label],
    ['Log', blok.log_label],
    ['Save', blok.save_label],
  ].filter(([, v]) => v);
  return (
    <Box {...editable(blok)}>
      <LabelRow label="Interactive" code={blok.field_code} />
      <Box sx={{ display: 'grid', gridTemplateColumns: '110px 1fr', rowGap: 0.4, columnGap: 1 }}>
        {rows.map(([k, v]) => (
          <Box key={String(k)} sx={{ display: 'contents' }}>
            <Typography sx={{ fontSize: 11.5, color: COLORS.placeholder }}>{k}</Typography>
            <Typography sx={{ fontSize: 13, color: COLORS.text }}>{v}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/** A block whose component this preview does not know — shown, not swallowed. */
function UnknownField({ blok }: { blok: FieldBlok }) {
  return (
    <Box {...editable(blok)} sx={{ ...fieldBoxSx, borderStyle: 'dashed' }}>
      <Typography sx={{ fontSize: 12.5, color: COLORS.muted }}>
        {blok.component || 'unknown block'}
        <CodeTag code={blok.field_code} />
      </Typography>
    </Box>
  );
}

function FieldRow({ blok }: { blok: FieldBlok }) {
  switch (blok.component) {
    case 'taskv2_calendar_block': return <CalendarField blok={blok} />;
    case 'taskv2_dropdown_block': return <DropdownField blok={blok} />;
    case 'taskv2_input_block': return <InputField blok={blok} />;
    case 'taskv2_attestation_block': return <AttestationField blok={blok} />;
    case 'taskv2_interactive_block': return <InteractiveField blok={blok} />;
    default: return <UnknownField blok={blok} />;
  }
}

export function CompletionUiPreview({ blok: raw }: { blok: CompletionUiBlok }) {
  // CPT-1254: Task V2 text fields may be richtext docs; every slot below renders a
  // string, so flatten docs to text once here.
  const blok = flattenRichTextDeep(raw);
  const fields = Array.isArray(blok.fields) ? blok.fields : [];
  return (
    <Box
      {...editable(blok)}
      sx={{
        maxWidth: 420, mx: 'auto', bgcolor: COLORS.ground,
        border: `1px solid ${COLORS.border}`, borderRadius: '16px',
        p: 2.5, display: 'flex', flexDirection: 'column', gap: 2,
        boxShadow: '0 2px 12px rgba(13,28,61,0.08)',
      }}
    >
      <Box>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: COLORS.text, lineHeight: 1.3 }}>
          {blok.title || 'Untitled completion screen'}
        </Typography>
        {blok.description ? (
          <Typography sx={{ fontSize: 13.5, color: COLORS.muted, mt: 0.75, lineHeight: 1.5 }}>
            {blok.description}
          </Typography>
        ) : null}
      </Box>

      {fields.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: COLORS.placeholder, fontStyle: 'italic' }}>
          This story has no field blocks, so the member sees only the title, description and button.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
          {fields.map((f, i) => <FieldRow key={f._uid ?? i} blok={f} />)}
        </Box>
      )}

      {blok.warning_label ? (
        <Typography sx={{ fontSize: 12, color: '#8A6D00', bgcolor: '#FFF8E1', p: 1, borderRadius: '8px' }}>
          {blok.warning_label}
        </Typography>
      ) : null}

      <Box sx={{
        bgcolor: COLORS.accent, color: '#fff', borderRadius: '24px',
        py: 1.25, textAlign: 'center', fontSize: 14.5, fontWeight: 600,
      }}>
        {blok.cta_label || 'Mark complete'}
      </Box>
    </Box>
  );
}

export default CompletionUiPreview;
